import React from 'react'
import { DelayedTextInput } from '../../../components/form/textInput'
import { User } from '../../../providers/context'
import { TeacherConferenceData, ConferenceLocation } from '../components/conferenceComponents'
import { LocalTable, VisibleColumnDef } from '../../../components/table'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../../request'
import { Popups } from '../../../providers/popups'
import { IconEdit } from '../../../icons/library'
import { Loader } from '../../../components/simple/loader'

type TimeSlot = {
  date: string
  attended: number
  interpreted: number
  total: number
  possible: number
}

type GradeAppointments = {
  grade: string
  attended: number
  interpreted: number
  appointments: number
  uniqueStudents: number
  studentsInGrade: number
}

type LanguageAppointments = {
  language: string
  attended: number
  appointments: number
  uniqueStudents: number
  studentsWithLanguage: number
}

export const StatsTab: React.FC<{ conference: TeacherConferenceData; user: User }> = ({ conference, user }) => {
  const [searchTimeSlots, setSearchTimeSlots] = React.useState('')
  const [searchGrades, setSearchGrades] = React.useState('')
  const [searchLanguages, setSearchLanguages] = React.useState('')

  const { data, reload } = useRequest<{
    timeSlots: TimeSlot[]
    grades: GradeAppointments[]
    languages: LanguageAppointments[]
  }>(
    '/frontend_api/getConferenceStatsTables',
    React.useMemo(
      () => ({
        conference_id: conference.id,
      }),
      [conference.id]
    )
  )

  const timeSlotColumns = React.useMemo<VisibleColumnDef<TimeSlot>[]>(
    () => [
      {
        header: 'Date',
        accessorKey: 'date',
      },
      {
        header: 'Attended',
        accessorKey: 'attended',
      },
      {
        header: 'Interpreted',
        accessorKey: 'interpreted',
      },
      {
        header: 'Total',
        accessorKey: 'total',
      },
      {
        header: 'Possible',
        accessorKey: 'possible',
      },
    ],
    [conference.id, reload]
  )
  const gradeColumns = React.useMemo<VisibleColumnDef<GradeAppointments>[]>(
    () => [
      {
        header: 'Grade',
        accessorKey: 'grade',
      },
      {
        header: 'Attended',
        accessorKey: 'attended',
      },
      {
        header: 'Interpreted',
        accessorKey: 'interpreted',
      },
      {
        header: 'Appointments',
        accessorKey: 'appointments',
      },
      {
        header: 'Unique Students',
        accessorKey: 'uniqueStudents',
      },
      {
        header: 'Students in Grade',
        accessorKey: 'studentsInGrade',
      },
    ],
    [conference.id, reload]
  )
  const languageColumns = React.useMemo<VisibleColumnDef<LanguageAppointments>[]>(
    () => [
      {
        header: 'Language',
        accessorKey: 'language',
      },
      {
        header: 'Attended',
        accessorKey: 'attended',
      },
      {
        header: 'Appointments',
        accessorKey: 'appointments',
      },
      {
        header: 'Unique Students',
        accessorKey: 'uniqueStudents',
      },
      {
        header: 'Students with Language',
        accessorKey: 'studentsWithLanguage',
      },
    ],
    [conference.id, reload]
  )

  return (
    <div className="mt-4 flex min-h-0 flex-col gap-2 overflow-y-scroll">
      <div className="flex flex-col">
        <div className="mt-2 flex items-center gap-2">
          <b className="text-lg">Appointments By Time Slot</b>
          <DelayedTextInput
            placeholder="Search"
            value={searchTimeSlots}
            onChange={setSearchTimeSlots}
            className="ml-auto rounded-full"
          />
        </div>
        {data ? <LocalTable search={searchTimeSlots} columns={timeSlotColumns} data={data.timeSlots} /> : <Loader />}
        <div className="mt-6 flex items-center gap-2">
          <b className="text-lg">Appointments By Grade</b>
          <DelayedTextInput
            placeholder="Search"
            value={searchGrades}
            onChange={setSearchGrades}
            className="ml-auto rounded-full"
          />
        </div>
        {data ? <LocalTable search={searchGrades} columns={gradeColumns} data={data.grades} /> : <Loader />}
        <div className="mt-6 flex items-center gap-2">
          <b className="text-lg">Appointments By Language</b>
          <DelayedTextInput
            placeholder="Search"
            value={searchLanguages}
            onChange={setSearchLanguages}
            className="ml-auto rounded-full"
          />
        </div>
        {data ? <LocalTable search={searchLanguages} columns={languageColumns} data={data.languages} /> : <Loader />}
      </div>
    </div>
  )
}
